import { ElementRef, QueryList, ViewChildren } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { InstanceRights, Roles, RestrictService } from '../Common/Permissions/restrict.service';
import { DescriptorHelperService } from './descriptor.helper.service';
import { DescriptorPicklistService } from './descriptor.picklist.service';
import { DescriptorService } from './descriptor.service';
import { UserInstanceService } from '../User/userInstance.service';

import { WsAgGridWrapperComponent } from '../Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import { FormsModule } from '@angular/forms';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../Compliance/AgGrid';
import { ApplyColumnStateParams, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import {
    AgGridActionCellRendererComponent,
    AgGridActionCellRendererParams,
} from '../Compliance/AgGrid/CellRenderers/agGridActionCellRenderer.component';
import { AgGridRowCountComponent } from '../Compliance/AgGrid/Row-Count/agGridRowCount.component';
import { HelpModule } from '../UI-Lib/Help-Tooltip/help.module';
import { SelectComponent } from '../UI-Lib/Select/select.component';
import { WeissmanModalService } from '../Compliance/WeissmanModalService';
import { PropertyCharacteristicDetailComponent } from './Detail/propertyCharacteristicDetail.component';
import { MessageModalService } from '../UI-Lib/Message-Box/messageModal.service';
import {
    AgGridTooltipCellRendererComponent,
    AgGridTooltipCellRendererParams,
} from '../Compliance/AgGrid/CellRenderers/agGridTooltipCellRenderer.component';
import { AgGridCheckboxCellRendererComponent } from '../Compliance/AgGrid/CellRenderers/agGridCheckboxCellRender.component';
import { PicklistModalComponent } from './Picklist-Modal/picklistModal.component';
import {
    AgGridLinkCellRendererComponent,
    AgGridLinkCellRendererParams,
} from '../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';

import * as _ from 'lodash';
import { AgGridYesNoFloatingFilterComponent } from '../Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component';

enum DescriptorFieldTypes {
    Picklist = Core.DescriptorFieldTypes.Picklist as number,
}

@Component({
    selector: 'property-characteristics',
    templateUrl: './property.characteristics.component.html',
    styleUrls: ['./property.characteristics.component.scss'],
    imports: [FormsModule, WsAgGridWrapperComponent, AgGridRowCountComponent, HelpModule, SelectComponent],
})
export class PropertyCharacteristicsComponent implements OnInit {
    constructor(
        private readonly _descriptorHelperService: DescriptorHelperService,
        private readonly _descriptorService: DescriptorService,
        private readonly _messageModalService: MessageModalService,
        private readonly _descriptorPicklistService: DescriptorPicklistService,
        private readonly _restrictService: RestrictService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _weissmanModalService: WeissmanModalService,
    ) {}

    @ViewChildren('formRow') rows: QueryList<ElementRef>;

    dataLoading: boolean = false;
    initialized: boolean = false;
    canEdit: boolean = false;
    hasComplianceFeatures: boolean = false;
    selectedCategoryId: Core.DescriptorCategoryEnum;
    descriptors: Core.DescriptorDTO[];
    categories: Weissman.Model.Descriptors.DescriptorCategory[] = [];
    usages: Weissman.Model.Descriptors.DescriptorUsage[];
    fieldTypes: Weissman.Model.Descriptors.DescriptorFieldType[];
    assetUsages: Weissman.Model.Descriptors.DescriptorUsage[];
    picklist: Core.DescriptorPickListModel[];
    FieldTypeEnum: typeof DescriptorFieldTypes = DescriptorFieldTypes;
    deletingDescriptorId: number;
    isRyanInstance: boolean;

    gridId: System.Guid = '081dd85b-5372-416c-add8-b64460b0d218';
    gridApi: GridApi;
    gridOptions = new AgGridOptionsBuilder()
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .build();

    async ngOnInit(): Promise<void> {
        this.dataLoading = true;
        this.isRyanInstance =
            this._userInstanceService.RyanInstanceId == this._userInstanceService.getSelectedInstance().instanceId;
        this.canEdit = this._restrictService.isInRole(Roles.PROPCHAREDIT);
        this.hasComplianceFeatures = this._restrictService.hasInstanceRight(InstanceRights.COMPLIANCEFEATURESVIEW);

        try {
            const categories = await this._descriptorHelperService.getCategories();
            this.categories = _.sortBy(categories, 'name');
            this.selectedCategoryId = this.categories[0].descriptorCategoryID;

            if (!this.hasComplianceFeatures) {
                this.categories = _.reject(this.categories, (x) => _.startsWith(x.name, 'Asset'));
            }

            this.fieldTypes = await this._descriptorHelperService.getFieldTypes();
            this.usages = await this._descriptorHelperService.getUsages();

            // override the select values for assets
            this.assetUsages = JSON.parse(JSON.stringify(this.usages));
            this.assetUsages = _.map(this.assetUsages, (usage) => {
                if (usage.name === 'Frequent') {
                    usage.name = 'Required';
                } else if (usage.name === 'Occasional') {
                    usage.name = 'Optional';
                }

                return usage;
            });
        } finally {
            this.dataLoading = false;
            this.initialized = true;
        }
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        this._setColumnDefinitions();

        this._loadDescriptors();
    }

    async getDescriptors(selectedCategoryId: number): Promise<void> {
        this.selectedCategoryId = selectedCategoryId;
        this.dataLoading = true;
        this.gridApi.setGridOption('loading', true);

        try {
            await this._loadDescriptors();
        } finally {
            this.dataLoading = false;
            this.gridApi.setGridOption('loading', false);
        }
    }

    getDescriptorUsage(id: number): string {
        const usage = this.usages.find((x) => x.descriptorUsageID === id);
        return usage ? usage.name : '';
    }

    getAssetDescriptorUsage(id: number): string {
        const assetUsage = this.assetUsages.find((x) => x.descriptorUsageID === id);
        return assetUsage ? assetUsage.name : '';
    }

    getDescriptorFieldType(id: number): string {
        const fieldType = this.fieldTypes.find((x) => x.descriptorFieldTypeID === id);
        return fieldType ? fieldType.name : '';
    }

    async editDescriptor(descriptor: Core.DescriptorDTO, isNew: boolean = false): Promise<void> {
        if (!this.canEdit) {
            return;
        }

        const params = {
            descriptor: descriptor,
            usages: this.usages,
            assetUsages: this.assetUsages,
            fieldTypes: this.fieldTypes,
            hasComplianceFeatures: this.hasComplianceFeatures,
            isNew: isNew,
        };

        const result = await this._weissmanModalService.showAsync(
            PropertyCharacteristicDetailComponent,
            params,
            'modal-lg',
        );

        if (result) {
            if (!isNew) {
                const index = this.descriptors.findIndex((x) => x.descriptorID === result.descriptorID);
                this.descriptors[index] = result;
            } else {
                this.descriptors.push(result);
            }

            this.gridApi.setGridOption('rowData', this.descriptors);
        }
    }

    async addDescriptor(): Promise<void> {
        const newDescriptor = {
            assetUsageID: Core.DescriptorUsageEnum.Never,
            assessorUsageID: Core.DescriptorUsageEnum.Never,
            collectorUsageID: Core.DescriptorUsageEnum.Never,
            siteUsageID: Core.DescriptorUsageEnum.Never,
            parcelUsageID: Core.DescriptorUsageEnum.Never,
            companyUsageId: Core.DescriptorUsageEnum.Never,
            arccUsageID: Core.DescriptorUsageEnum.Never,
            categoryID: this.selectedCategoryId,
            fieldTypeID: Core.DescriptorFieldTypes.Number,
            formatID: 1,
            pickListGroupID: null,
            enabled: true,
            sequenceNumber: _.get(_.maxBy(this.descriptors, 'sequenceNumber'), 'sequenceNumber', 0) + 1,
        } as Core.DescriptorDTO;

        await this.editDescriptor(newDescriptor, true);
    }

    async deleteDescriptor(descriptor: Core.DescriptorDTO): Promise<void> {
        if (!this.canEdit) {
            return;
        }

        try {
            await this._messageModalService.confirm(`Are you sure you want to delete ${descriptor.name}?`);
        } catch {
            return;
        }

        this.deletingDescriptorId = descriptor.descriptorID;

        try {
            const deleteResult = await this._descriptorService.deleteById(descriptor.descriptorID);

            if (deleteResult) {
                descriptor.enabled = false;
            } else {
                _.remove(this.descriptors, descriptor);
                this.gridApi.setGridOption('rowData', this.descriptors);
            }
        } finally {
            this.deletingDescriptorId = undefined;
        }
    }

    async showPicklistValuesModal(descriptor: Core.DescriptorDTO): Promise<void> {
        if (descriptor.pickListGroupID) {
            const picklist = await this._descriptorPicklistService.getByPicklistGroupId(descriptor.pickListGroupID);
            this.picklist = _.sortBy(picklist, 'name');
        }
        await this._weissmanModalService.showAsync(
            PicklistModalComponent,
            { picklist: this.picklist || [] },
            'modal-sm',
        );
    }

    shouldDisableEditButtons(descriptor: Core.DescriptorDTO): boolean {
        return (
            !this.canEdit || descriptor.isSystemControlled || descriptor.descriptorID === this.deletingDescriptorId
        );
    }

    private async _loadDescriptors(): Promise<void> {
        const descriptors = await this._descriptorService.getByCategoryId(this.selectedCategoryId);
        this.descriptors = descriptors.map((x) => {
            x.formatID = x.formatID || 1;
            return x;
        });
        this.gridApi.setGridOption('rowData', this.descriptors);
    }

    private _setColumnDefinitions(): void {
        const columns: ColDef[] = [
            {
                headerName: 'Characteristic Name',
                field: 'name',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Company Usage',
                field: 'companyUsageId',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorUsage(params.data.companyUsageId);
                },
                valueFormatter: (params) => {
                    return this.getDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'Site Usage',
                field: 'siteUsageID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorUsage(params.data.siteUsageID);
                },
                valueFormatter: (params) => {
                    return this.getDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'Parcel Usage',
                field: 'parcelUsageID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorUsage(params.data.parcelUsageID);
                },
                valueFormatter: (params) => {
                    return this.getDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'Assessor Usage',
                field: 'assessorUsageID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorUsage(params.data.assessorUsageID);
                },
                valueFormatter: (params) => {
                    return this.getDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'Asset Usage',
                field: 'assetUsageID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getAssetDescriptorUsage(params.data.assetUsageID);
                },
                valueFormatter: (params) => {
                    return this.getAssetDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'ARCC Usage',
                field: 'arccUsageID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorUsage(params.data.arccUsageID);
                },
                valueFormatter: (params) => {
                    return this.getDescriptorUsage(params.value);
                },
            },
            {
                headerName: 'Type',
                field: 'fieldTypeID',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    onClickAsync: (params) => this.showPicklistValuesModal(params.data as Core.DescriptorDTO),
                    getButtonLink: (params) => {
                        return `${this.getDescriptorFieldType(params.value)} (Values...)`;
                    },
                    getText: (params) => this.getDescriptorFieldType(params.value),
                    isDisabled: (params) => !(params.data.fieldTypeID === DescriptorFieldTypes.Picklist),
                } as AgGridLinkCellRendererParams,
                filterValueGetter: (params) => {
                    return this.getDescriptorFieldType(params.data.fieldTypeID);
                },
            },
            {
                headerName: 'Comments',
                field: 'notes',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridTooltipCellRendererComponent,
                cellRendererParams: {
                    getTooltipText: (params) => params.value,
                    cellIcon: 'fa-commenting',
                    isMultiline: (params) => params.value?.length > 50,
                } as AgGridTooltipCellRendererParams,
            },
            {
                headerName: 'Enabled',
                field: 'enabled',
                width: AgGridColumns.textColumnWidth,
                filter: 'agYesNoColumnFilter',
                filterParams: AgGridFilterParams.yesNoFilterParams,
                floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
                floatingFilterComponentParams: AgGridFilterParams.yesNoFilterParams,
                cellRenderer: AgGridCheckboxCellRendererComponent,
                cellRendererParams: {
                    isVisible: () => true,
                    canEdit: () => false,
                },
                filterValueGetter: (params) => (params.data.enabled ? 'Yes' : 'No'),
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(2),
                minWidth: AgGridColumns.getActionColumnWidth(2),
                maxWidth: AgGridColumns.getActionColumnWidth(2),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: AgGridActionCellRendererComponent,
                cellRendererParams: {
                    buttonConfigs: [
                        {
                            iconClass: 'fa-pencil',
                            onClick: (params) => this.editDescriptor(params.data),
                            isShown: () => true,
                            disabled: (params) => this.shouldDisableEditButtons(params.data),
                            buttonClass: 'btn-primary btn-primary',
                            helpContentId: 'app.edit',
                        },
                        {
                            iconClass: 'fa-trash',
                            onClick: (params) => this.deleteDescriptor(params.data),
                            isShown: () => true,
                            disabled: (params) => this.shouldDisableEditButtons(params.data),
                            buttonClass: 'btn-primary btn-danger',
                            helpContentId: 'app.delete',
                        },
                    ],
                } as AgGridActionCellRendererParams,
            },
        ];

        if (!this.hasComplianceFeatures) {
            const assetUsageColIndex = columns.findIndex((col) => col.headerName === 'Asset Usage');
            if (assetUsageColIndex > -1) {
                columns.splice(assetUsageColIndex, 1);
            }
        }

        if (!this.isRyanInstance) {
            const arccUsageColIndex = columns.findIndex((col) => col.headerName === 'ARCC Usage');
            if (arccUsageColIndex > -1) {
                columns.splice(arccUsageColIndex, 1);
            }
        }

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'name', sort: 'asc' }],
            defaultState: { sort: null },
        };

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
    }
}
